import AuthStorage from 'app/modules/authStorage';
import { WalletStorage } from 'app/modules/walletStorage';
import {
  checkValidAddress,
  connectWallet,
  connectWalletV2ForClaim,
  submitWalletCode,
} from 'app/services/connectWalletService';
import {
  getMetamaskAddressStatus,
  transferNft,
} from 'app/services/nftDetailService';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router';
import {
  connectMetamaskWallet,
  metamaskErrorMessage,
  metamaskHomepageUrl,
  requestConnectWithList,
  signMessage,
} from 'utils/metamaskInteract';
import { useWindowWidthContext } from '../WindowWidthContext';
import { getSigningMessage, walletFlowType, walletTypes } from './constant';

type ConnectWalletContextProps = {
  walletAddress?: string | null;
  claimWalletAddress?: string;
  flowType?: string | null;
  isEmailSubmitted?: boolean;
  isLoading?: boolean;
  isNftClaimFlow?: boolean;
  error?: string;
  email?: string;
  walletType?: string;
  getCode?: (...args: any[]) => void;
  onCodeSubmit?: (...args: any[]) => void;
  onContact?: (...args: any[]) => void;
  clearError?: (...args: any[]) => void;
  onEmailSubmit?: (...args: any[]) => void;
  onConnectClick?: (...args: any[]) => void;
  onMetamaskClick?: (...args: any[]) => void;
  onNasWalletClick?: (...args: any[]) => void;
  isRedirectLPError?: boolean;
  closeRedirectLPModal?: (...args: any[]) => void;
  isMetamskErrorModal?: boolean;
  closeMetamaskErrorModal?: (...args: any[]) => void;
  resetFlow?: (...args: any[]) => void;
  onDisconnect?: (...args: any[]) => void;
  onAlternateAddressSelect?: (...args: any[]) => void;
  setNftClaiDetails?: (...args: any[]) => void;
  userWalletList?: string[];
};

export const ConnectWalletContext = createContext<ConnectWalletContextProps>(
  {},
);

const ConnectWalletContextProvider = ({ children }) => {
  const { isDesktopView } = useWindowWidthContext();

  const history = useHistory();
  const queryParams = new URLSearchParams(history.location.search);
  const learnerId = queryParams?.get('learnerId');

  const [walletAddress, setWalletAddress] = useState<string | null>(
    WalletStorage.getWalletAddress(),
  );
  const [claimWalletAddress, setClaimWalletAddress] = useState<string>('');
  const [flowType, setFlowType] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isEmailSubmitted, setIsEmailSubmitted] = useState<boolean>(false);

  const [isRedirectLPError, setIsRedirectLPError] = useState<boolean>(false);
  const [isMetamskErrorModal, setIsMetamskErrorModal] = useState<boolean>(
    false,
  );

  const [nftClaimDetails, setNftClaiDetails] = useState<{
    tokenId: string;
    contractAddress: string;
  }>({ tokenId: '', contractAddress: '' });
  const [isModalNotClosable, setIsModalNotClosable] = useState<boolean>(false);
  const [isNftClaimFlow, setIsNftClaimFlow] = useState<boolean>(false);
  const [userWalletList, setUserWalletList] = useState<string[]>([]);
  const [walletType, setWalletType] = useState<string>('');

  const onConnectClick = (
    { modalsNotClosable, claimNftFlow } = {
      modalsNotClosable: false,
      claimNftFlow: false,
    },
  ) => {
    //make the flow unskippable
    if (modalsNotClosable) setIsModalNotClosable(true);

    //to accomodate for minting flow
    if (claimNftFlow) setIsNftClaimFlow(true);

    // setFlowType(walletFlowType.ADDRESS_CHANGE);
    setFlowType(walletFlowType.WALLET_SELECT);
  };

  const onNasWalletClick = () => {
    setFlowType(walletFlowType.NAS_WALLET);
    setWalletType(walletTypes.NAS_WALLET);
  };

  const clearError = useCallback(() => {
    if (!!error) setError('');
  }, [error]);

  const onContact = () => {
    const userback = window.Userback;
    if (userback) userback.open();
  };

  const onEmailSubmit = async e => {
    try {
      e.preventDefault();
      setIsLoading(true);

      const email = e.target?.email?.value;

      if (!email) return;

      const params = {
        email,
      };

      await getCode({ params });

      setIsEmailSubmitted(true);
      setEmail(email);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCode = async dataParams => {
    const params = dataParams?.params ?? { email };

    const sendCodeToUserResponse = isNftClaimFlow
      ? await connectWalletV2ForClaim(params)
      : await connectWallet(params);

    const { data, error } = sendCodeToUserResponse;

    if (error)
      //@ts-ignore
      throw new Error(error?.response?.data?.error ?? 'Something went wrong!');

    return data;
  };

  const onCodeSubmit = useCallback(
    async ({ value, formEle }) => {
      try {
        setIsLoading(true);

        const params = {
          verification_code: value,
          email,
        };
        const { error, data } = await submitWalletCode(params);

        if (error)
          throw new Error(
            //@ts-ignore
            error?.response?.data?.error || 'Something went wrong!',
          );

        if (isNftClaimFlow) {
          setClaimWalletAddress(data?.address);
          await claimNftCall({ address: data?.address });
          return;
        }

        setWalletAddress(data?.address);
        setFlowType(null);
        AuthStorage.setTokens(data?.access_token, data?.refresh_token);
        WalletStorage.setWalletAddress({ address: data?.address });
        window.location.href = '/tokens';
      } catch (e) {
        if (e instanceof Error) {
          formEle.reset();
          setError(e.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, isNftClaimFlow],
  );

  const onMetamaskClick = async () => {
    try {
      setWalletType(walletTypes.METAMASK);
      const { address, error } = await connectMetamaskWallet();

      //@ts-ignore
      if (!!error) throw new Error(error);

      if (isNftClaimFlow) {
        await onMetamaskAddressVerify({ address });
        return;
      }

      //@ts-ignore
      const { error: addressError, data } = await checkValidAddress({
        walletAddress: address,
      });

      if (!!addressError) {
        setIsRedirectLPError(true);
        return;
      }

      setWalletAddress(address);
      setFlowType(null);
      AuthStorage.setTokens(data?.accessToken, data?.refreshToken);
      WalletStorage.setWalletAddress({ address });

      window.location.href = '/tokens';
    } catch (e) {
      if (e instanceof Error) {
        const message = e.message;

        if (message === metamaskErrorMessage.not_installed) {
          if (!isDesktopView) setIsMetamskErrorModal(true);

          window.open(metamaskHomepageUrl, '_blank');
        }
      }
    }
  };

  const onMetamaskAddressVerify = async ({ address }) => {
    try {
      setIsLoading(true);

      const params = {
        learnerId,
        walletType: walletTypes.METAMASK,
      };

      const { data, error } = await getMetamaskAddressStatus(params);

      // @ts-ignore
      if (error) throw new Error(error);

      const userAddresses =
        data?.map(wallet => wallet?.walletAddress?.toLowerCase()) ?? [];

      const isCurrentAddressIncluded = userAddresses?.find(
        walletAddress => walletAddress === address,
      );

      // if address is present in the db
      if (!!isCurrentAddressIncluded || !userAddresses.length) {
        const { status, message } = await signMessage({
          message: getSigningMessage({ address }),
        });

        if (status === 'error') {
          setError(message);
          setFlowType(walletFlowType.ERROR);
          return;
        }

        setClaimWalletAddress(address);
        await claimNftCall({ address });
        return;
      }

      // if address is not present in the db
      // ask them to change the address
      setUserWalletList([address, ...userAddresses]);
      setFlowType(walletFlowType.ADDRESS_CHANGE);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
        setFlowType(walletFlowType.ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const claimNftCall = async ({ address }) => {
    try {
      setIsLoading(true);

      const { error } = await transferNft({
        contractAddress: nftClaimDetails.contractAddress,
        walletAddress: address,
        tokenId: nftClaimDetails.tokenId,
        learnerId,
      });

      // @ts-ignore
      if (error) throw new Error(error);
    } catch (error) {
      if (error instanceof Error) setError(error.message);
    } finally {
      setFlowType(walletFlowType.CLAIM_NFT);
      setIsLoading(false);
    }
  };

  const onAlternateAddressSelect = async ({ ref }) => {
    try {
      setIsLoading(true);

      const { address, error } = await requestConnectWithList();

      const selectedAddress = ref?.value;

      if (error) throw new Error(error);

      if (address !== selectedAddress)
        throw new Error('Selected wallet address is not the one connected!');

      const { status, message } = await signMessage({
        message: getSigningMessage({ address }),
      });

      if (status === 'error') throw new Error(message);

      setClaimWalletAddress(address);
      await claimNftCall({ address });
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
        setFlowType(walletFlowType.ERROR);
      }
    } finally {
      setIsLoading(true);
    }
  };

  const closeRedirectLPModal = () => setIsRedirectLPError(false);
  const closeMetamaskErrorModal = () => setIsMetamskErrorModal(false);

  const onDisconnect = () => {
    WalletStorage.clearWalletAddress();
    AuthStorage.deAuthenticateUser();

    setWalletAddress(null);
    setFlowType(null);

    window.location.href = '/';
  };

  const resetFlow = ({ forced } = { forced: false }) => {
    if (isModalNotClosable && !forced) return;

    setFlowType(null);
    setIsEmailSubmitted(false);
    setError('');
    setIsRedirectLPError(false);
  };

  const value = {
    walletAddress,
    claimWalletAddress,

    flowType,
    resetFlow,

    email,
    error,
    clearError,
    isLoading,
    isEmailSubmitted,
    onEmailSubmit,

    onContact,
    getCode,
    onCodeSubmit,

    isRedirectLPError,
    closeRedirectLPModal,
    isMetamskErrorModal,
    closeMetamaskErrorModal,

    onConnectClick,
    onMetamaskClick,
    onNasWalletClick,
    onDisconnect,

    walletType,
    isNftClaimFlow,
    userWalletList,
    onAlternateAddressSelect,
    setNftClaiDetails,
  };

  return (
    <ConnectWalletContext.Provider value={value}>
      {children}
    </ConnectWalletContext.Provider>
  );
};

export const useConnectWallet = () => useContext(ConnectWalletContext);

export default ConnectWalletContextProvider;
